import {
  canAccessMediaLibrary,
  hasAssistantRole,
  MEDIA_LIBRARY_APP_ID,
  type AssistantRole
} from '../../auth/roles';
import type { HubDefinition, StandaloneAppDefinition, ToolDefinition, ToolStatus } from './types';

export interface RegistrySearchEntry {
  id: string;
  name: string;
  description: string;
  path: string;
  icon: string;
  kind: 'hub' | 'tool' | 'standalone';
  searchText: string;
}

export interface CatalogContractEntry {
  id: string;
  minRole: string;
}

export interface CatalogContractDifference {
  id: string;
  issue: 'missingClientEntry' | 'missingServerEntry' | 'roleMismatch';
  clientRole?: AssistantRole;
  serverRole?: string;
}

function isReady(status: ToolStatus): boolean {
  return status === 'ready';
}

function resolveToolRole(hub: HubDefinition, tool: ToolDefinition): AssistantRole {
  return tool.minRole ?? hub.minRole;
}

function canAccessRole(roles: readonly string[], minRole: AssistantRole): boolean {
  return hasAssistantRole([...roles], minRole);
}

export function getVisibleTools(
  hub: HubDefinition,
  roles: readonly string[]
): readonly ToolDefinition[] {
  if (!isReady(hub.status)) return [];
  return hub.tools.filter(
    (tool) => isReady(tool.status) && canAccessRole(roles, resolveToolRole(hub, tool))
  );
}

function isHubVisible(hub: HubDefinition, roles: readonly string[]): boolean {
  return getVisibleTools(hub, roles).length > 0;
}

export function getVisibleHubs(
  hubs: readonly HubDefinition[],
  roles: readonly string[]
): readonly HubDefinition[] {
  return hubs.filter((hub) => isHubVisible(hub, roles));
}

export function getVisibleStandaloneApps(
  apps: readonly StandaloneAppDefinition[],
  roles: readonly string[]
): readonly StandaloneAppDefinition[] {
  return apps.filter((app) => {
    if (!isReady(app.status)) return false;
    if (app.id === MEDIA_LIBRARY_APP_ID) return canAccessMediaLibrary([...roles]);
    return canAccessRole(roles, app.minRole);
  });
}

export function getDefaultToolForHub(
  hub: HubDefinition,
  roles: readonly string[]
): ToolDefinition | null {
  return getVisibleTools(hub, roles)[0] ?? null;
}

export function findToolByPath(
  hubs: readonly HubDefinition[],
  path: string
): { hub: HubDefinition; tool: ToolDefinition } | null {
  const normalized = normalizePath(path);
  for (const hub of hubs) {
    const tool = hub.tools.find((candidate) => normalizePath(candidate.path) === normalized);
    if (tool) return { hub, tool };
  }
  return null;
}

export function buildSearchEntries(
  hubs: readonly HubDefinition[],
  standaloneApps: readonly StandaloneAppDefinition[],
  roles: readonly string[]
): readonly RegistrySearchEntry[] {
  const entries: RegistrySearchEntry[] = [];

  for (const hub of getVisibleHubs(hubs, roles)) {
    const tools = getVisibleTools(hub, roles);
    entries.push({
      id: hub.id,
      name: hub.name,
      description: hub.description,
      path: hub.path,
      icon: hub.icon,
      kind: 'hub',
      searchText: createSearchText([hub.name, hub.description, ...tools.flatMap(toolSearchParts)])
    });
    for (const tool of tools) {
      entries.push({
        id: tool.id,
        name: tool.name,
        description: tool.description,
        path: tool.path,
        icon: tool.icon,
        kind: 'tool',
        searchText: createSearchText(toolSearchParts(tool))
      });
    }
  }

  for (const app of getVisibleStandaloneApps(standaloneApps, roles)) {
    entries.push({
      id: app.id,
      name: app.name,
      description: app.description,
      path: app.path,
      icon: app.icon,
      kind: 'standalone',
      searchText: createSearchText([app.name, app.description, ...app.keywords, ...app.aliases])
    });
  }

  return entries;
}

export function validateRegistry(hubs: readonly HubDefinition[]): readonly string[] {
  const errors: string[] = [];
  const ids = new Set<string>();
  const paths = new Set<string>();

  for (const hub of hubs) {
    addUnique(errors, ids, hub.id, 'id');
    addUnique(errors, paths, normalizePath(hub.path), 'path');
    if (!hub.path.startsWith('/apps/')) {
      errors.push(`Hub "${hub.id}" must use an /apps/ path.`);
    }
    if (hub.tools.length === 0) {
      errors.push(`Hub "${hub.id}" must contain at least one tool.`);
    }

    const tabIds = new Set<string>();
    for (const tool of hub.tools) {
      addUnique(errors, ids, tool.id, 'id');
      addUnique(errors, paths, normalizePath(tool.path), 'path');
      addUnique(errors, tabIds, tool.tabId, `tab id in hub "${hub.id}"`);
      if (tool.hubId !== hub.id) {
        errors.push(`Tool "${tool.id}" references hub "${tool.hubId}" instead of "${hub.id}".`);
      }
      if (!normalizePath(tool.path).startsWith(`${normalizePath(hub.path)}/`)) {
        errors.push(`Tool "${tool.id}" path must be nested under hub "${hub.path}".`);
      }
    }
  }

  return errors;
}

/**
 * Diagnostic for the unavoidable TypeScript/.NET registry boundary. It is deliberately
 * data-only so API integration can activate it when the server switches to hub ids.
 */
export function compareCatalogContract(
  hubs: readonly HubDefinition[],
  standaloneApps: readonly StandaloneAppDefinition[],
  serverEntries: readonly CatalogContractEntry[]
): readonly CatalogContractDifference[] {
  const clientEntries = new Map<string, AssistantRole>();
  for (const hub of hubs) clientEntries.set(hub.id, hub.minRole);
  for (const app of standaloneApps) clientEntries.set(app.id, app.minRole);

  const serverById = new Map(serverEntries.map((entry) => [entry.id, entry]));
  const differences: CatalogContractDifference[] = [];

  for (const [id, clientRole] of clientEntries) {
    const server = serverById.get(id);
    if (!server) {
      differences.push({ id, issue: 'missingServerEntry', clientRole });
    } else if (server.minRole !== clientRole) {
      differences.push({
        id,
        issue: 'roleMismatch',
        clientRole,
        serverRole: server.minRole
      });
    }
  }

  for (const server of serverEntries) {
    if (!clientEntries.has(server.id)) {
      differences.push({
        id: server.id,
        issue: 'missingClientEntry',
        serverRole: server.minRole
      });
    }
  }

  return differences;
}

function normalizePath(path: string): string {
  const trimmed = path.trim();
  if (trimmed === '/') return trimmed;
  return trimmed.replace(/\/+$/, '');
}

function toolSearchParts(tool: ToolDefinition): string[] {
  return [tool.name, tool.shortName ?? '', tool.description, ...tool.keywords, ...tool.aliases];
}

function createSearchText(parts: readonly string[]): string {
  return parts
    .map((part) => part.trim().toLocaleLowerCase())
    .filter(Boolean)
    .join(' ');
}

function addUnique(errors: string[], values: Set<string>, value: string, label: string): void {
  if (values.has(value)) {
    errors.push(`Duplicate ${label}: "${value}".`);
    return;
  }
  values.add(value);
}
